import React, { useState, useEffect } from 'react';
import { Calendar, Check, X, Clock, User, FileText, CheckCircle, History, Home } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const WFHManagement = ({ onSidebarCountsRefresh }) => {
  const [wfhRequests, setWfhRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchWfhRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when status filter changes
  }, [filter]);

  const fetchWfhRequests = async () => {
    try {
      const url = filter === 'all' ? '/admin/wfh' : `/admin/wfh?status=${filter}`;
      const response = await api.get(url);
      setWfhRequests(response.data.wfhRequests || []);
    } catch (error) {
      console.error('Error fetching WFH applications:', error);
      toast.error('Failed to fetch WFH applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReject = async (wfhId, status, comments = '') => {
    try {
      await api.put(`/admin/wfh/${wfhId}/approve`, { status, comments });
      toast.success(`WFH application ${status} successfully!`);
      fetchWfhRequests();
      if (onSidebarCountsRefresh) {
        onSidebarCountsRefresh();
      }
    } catch (error) {
      const message = error.response?.data?.message || `Failed to ${status} WFH application`;
      toast.error(message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const avatarPalette = [
    { bg: 'bg-cyan-100', icon: 'text-cyan-600' },
    { bg: 'bg-blue-100', icon: 'text-blue-600' },
    { bg: 'bg-green-100', icon: 'text-green-600' },
    { bg: 'bg-purple-100', icon: 'text-purple-600' },
  ];

  const filteredRequests = wfhRequests.filter((wfh) => {
    if (filter === 'all') return true;
    return wfh.status === filter;
  });

  const filterTabs = [
    { key: 'all', label: 'All WFH', icon: FileText },
    { key: 'pending', label: 'Pending', icon: Clock },
    { key: 'approved', label: 'Approved', icon: CheckCircle },
    { key: 'rejected', label: 'Rejected', icon: X },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">WFH Applications</h1>
        <p className="text-gray-600">Review and manage employee work from home requests</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {filterTabs.map(({ key, label, icon: Icon }) => {
          const isActive = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                isActive
                  ? 'bg-cyan-600 border-cyan-600 text-white shadow-sm'
                  : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      <div className="space-y-4">
        {loading ? (
          <LoadingSpinner size="lg" className="py-8" />
        ) : filteredRequests.length > 0 ? (
          filteredRequests.map((wfh, idx) => {
            const avatar = avatarPalette[idx % avatarPalette.length];
            return (
              <div key={wfh._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-3 relative">
                {wfh.status !== 'pending' && (
                  <span className={`absolute top-5 right-5 inline-flex px-3 py-1 text-xs font-bold rounded-full uppercase ${getStatusColor(wfh.status)}`}>
                    {wfh.status}
                  </span>
                )}

                <div className="flex items-center space-x-3 mb-3">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className={`h-10 w-10 rounded-full ${avatar.bg} flex items-center justify-center`}>
                      <User className={`h-5 w-5 ${avatar.icon}`} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      {wfh.userId?.name || 'Unknown User'}
                    </h3>
                    <p className="text-sm text-gray-500 py-1">{wfh.userId?.email || 'N/A'}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center text-sm text-black-600 font-semibold">
                    <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span>
                      {new Date(wfh.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {new Date(wfh.startDate).toDateString() !== new Date(wfh.endDate).toDateString() && (
                        <> to {new Date(wfh.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                      )}
                    </span>
                  </div>

                  {wfh.wfhDays && wfh.wfhDays.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-bold text-cyan-700 uppercase mb-2">Days Breakdown:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {wfh.wfhDays.map((day, index) => (
                          <div key={index} className="flex flex-col space-y-1 bg-white rounded-lg px-3 py-2 border border-gray-200">
                            <span className="text-gray-700 font-semibold text-sm">
                              {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-cyan-100 text-cyan-700 w-fit">
                              {day.dayType === 'half' ? 'HALF' : 'FULL'} WFH
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center text-sm text-black-500 italic">
                    <History className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span>Applied: {new Date(wfh.createdAt).toLocaleDateString()}</span>
                  </div>

                  <div>
                    <p className="text-sm font-bold text-gray-900">Reason:</p>
                    <p className="text-sm text-blue-700 mt-1">{wfh.reason}</p>
                  </div>

                  {wfh.comments && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                      <p className="text-xs font-semibold text-amber-900">Admin Comments:</p>
                      <p className="text-sm text-amber-800 mt-1">{wfh.comments}</p>
                    </div>
                  )}
                </div>

                {wfh.status === 'pending' && (
                  <div className="flex space-x-3 mt-4">
                    <button
                      onClick={() => {
                        const comments = prompt('Add comments (optional):');
                        if (comments === null) return;
                        handleApproveReject(wfh._id, 'approved', comments);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-700 text-white rounded-lg hover:bg-green-800 text-sm font-semibold"
                    >
                      <Check className="h-4 w-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        const comments = prompt('Add comments (optional):');
                        if (comments === null) return;
                        handleApproveReject(wfh._id, 'rejected', comments);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-red-600 border-2 border-red-500 rounded-lg hover:bg-red-50 text-sm font-semibold"
                    >
                      <X className="h-4 w-4" />
                      Reject
                    </button>
                  </div>
                )}

                {wfh.approvedBy && (
                  <div className="text-xs text-gray-500 mt-3">
                    {wfh.status} by {wfh.approvedBy?.name || 'Unknown'}
                    <br />
                    {wfh.approvedAt ? new Date(wfh.approvedAt).toLocaleDateString() : ''}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <Home className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No WFH applications found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WFHManagement;
